import React, { useState, useEffect, useRef } from 'react';
import '../App.css';
import { useLocation, useNavigate } from 'react-router-dom';
import { Form, FormGroup, Button, Input, Label } from 'reactstrap';
import MyStorage from './util/storage';
import { Oval as Loader } from 'react-loader-spinner';
import { ToastContainer } from 'react-toastify';
import showToast from './util/toast';
import { db1, storage1 } from '../../firebase/firebase1';
import firebase from 'firebase/compat/app';
import moment from 'moment';
import Api from './api/api';
import add from '../assets/add.png';
import cross from '../assets/delete.png';
import Resizer from 'react-image-file-resizer';
import { easternWallTimeToEpoch, epochToEasternWallTime, formatEasternNow } from './util/eastern-time';

const AddQuiz = () => {
    const navigate = useNavigate();
    const location = useLocation();

    const model = location.state?.model;
    const editMode = model != null;

    const title = 'Evening Quiz with Doc D';
    const [question, setQuestion] = useState(editMode ? model.question : '');
    const [options, setOptions] = useState(
        editMode ? (model.options?.length === 4 ? model.options : ['', '', '', '']) : ['', '', '', '']
    );
    const [correctOptionIndex, setCorrectOptionIndex] = useState(
        editMode ? model.correctOptionIndex ?? 0 : 0
    );
    const [bonusFact, setBonusFact] = useState(editMode ? model.bonusFact || '' : '');
    const [loader, setLoader] = useState(false);
    const [isScheduled, setIsScheduled] = useState(editMode ? model.status === 'scheduled' : false);
    const [scheduledDateTime, setScheduledDateTime] = useState(() => {
        if (editMode && model.status === 'scheduled' && model.scheduledAt) {
            return epochToEasternWallTime(model.scheduledAt);
        }
        return '';
    });

    // Live current Eastern time, so the admin knows "now" while scheduling.
    const [easternNow, setEasternNow] = useState(formatEasternNow());
    useEffect(() => {
        const id = setInterval(() => setEasternNow(formatEasternNow()), 1000);
        return () => clearInterval(id);
    }, []);

    // Image upload state
    const addImageRef = useRef(null);
    const [imageFile, setImageFile] = useState(null);
    const [imagePreview, setImagePreview] = useState(editMode ? model.imageUrl || '' : '');

    if (!MyStorage.isLogin()) {
        navigate('/admin/login');
        return null;
    }

    const updateOption = (index, value) => {
        const updated = [...options];
        updated[index] = value;
        setOptions(updated);
    };

    const validate = () => {
        if (!question.trim()) { showToast('error', 'Enter question!'); return false; }
        if (options.some(o => !o.trim())) { showToast('error', 'Fill all 4 options!'); return false; }
        if (!bonusFact.trim()) { showToast('error', 'Enter bonus fact!'); return false; }
        if (!imageFile && !imagePreview) { showToast('error', 'Choose an image!'); return false; }
        if (isScheduled) {
            if (!scheduledDateTime) { showToast('error', 'Select a schedule date & time!'); return false; }
            if (easternWallTimeToEpoch(scheduledDateTime) <= Date.now()) { showToast('error', 'Scheduled time must be in the future!'); return false; }
        }
        return true;
    };

    const buildData = (resolvedImageUrl) => {
        const data = {
            title,
            imageUrl: resolvedImageUrl,
            question,
            options,
            correctOptionIndex,
            bonusFact,
            added_date: editMode ? (model.added_date || parseInt(moment() + '')) : parseInt(moment() + '')
        };
        if (isScheduled) {
            data.status = 'scheduled';
            data.scheduledAt = easternWallTimeToEpoch(scheduledDateTime);
        } else {
            data.status = editMode
                ? (model.status === 'scheduled' ? 'unattempted' : (model.status || 'unattempted'))
                : 'unattempted';
        }
        return data;
    };

    const uploadImageThenSave = (onUrl) => {
        const path = 'quizzes/' + Date.now() + '_' + imageFile.name;
        const uploadTask = storage1.ref(path).put(imageFile);
        uploadTask.on('state_changed',
            () => {},
            () => { showToast('error', 'Image upload failed!'); setLoader(false); },
            () => {
                storage1.ref(path).getDownloadURL().then(url => onUrl(url));
            }
        );
    };

    const sendNotification = (docId) => {
        const notifTitle = 'New Quiz by Liviq';
        const notifBody = 'Check out the new quiz by Liviq!';
        db1.collection('notifications').add({
            title: notifTitle,
            body: notifBody,
            type: 'quiz',
            docId: docId,
            added_date: firebase.firestore.FieldValue.serverTimestamp()
        });
        Api.sendPushToAll(notifTitle, notifBody, () => {}, () => {});
    };

    const saveToFirestore = (resolvedImageUrl) => {
        const data = buildData(resolvedImageUrl);
        if (editMode) {
            // Remove scheduledAt field if unscheduling an existing scheduled quiz
            const updateData = (!isScheduled && model.status === 'scheduled')
                ? { ...data, scheduledAt: firebase.firestore.FieldValue.delete() }
                : data;
            db1.collection('quizzes').doc(model.id).set(updateData, { merge: true })
                .then(() => { setLoader(false); showToast('success', 'Quiz saved!'); navigate(-1); })
                .catch(() => { setLoader(false); showToast('error', 'Save failed!'); });
        } else {
            db1.collection('quizzes').add(data)
                .then((doc) => {
                    setLoader(false);
                    if (isScheduled) {
                        showToast('success', 'Quiz scheduled!');
                    } else {
                        showToast('success', 'Quiz added!');
                        sendNotification(doc.id);
                    }
                    navigate(-1);
                })
                .catch(() => { setLoader(false); showToast('error', 'Save failed!'); });
        }
    };

    const handleSave = () => {
        if (!validate()) return;
        setLoader(true);
        if (imageFile) {
            uploadImageThenSave(saveToFirestore);
        } else {
            saveToFirestore(imagePreview);
        }
    };

    const resizeFile = (file) => new Promise(resolve => {
        Resizer.imageFileResizer(file, 1024, 1024, 'JPEG', 90, 0,
            uri => {
                resolve(uri);
            }, 'file');
    });

    const handleImageFileChange = async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        const resizedImage = await resizeFile(file);
        setImageFile(resizedImage);
        setImagePreview(URL.createObjectURL(resizedImage));
    };

    return (
        <div className='main'>
            <div className='titlebar' style={{ backgroundColor: '#f2f2f2', padding: 10, margin: -15 }}>
                <div style={{ color: 'blue' }}>{editMode ? 'Edit Quiz' : 'Add Quiz'}</div>
            </div>

            <div style={{ width: '100%', display: 'flex', flexDirection: 'column', padding: 0 }}>
                <div style={{ paddingTop: 30, paddingBottom: 100, display: 'flex', flexDirection: 'column', alignItems: 'center', height: '100vh', overflow: 'scroll' }}>
                    <Form className='form' style={{ padding: 20, backgroundColor: 'white', borderRadius: 10, minWidth: 600 }}>

                        <FormGroup>
                            <Label>Image</Label>
                            <div style={{ display: 'flex', alignItems: 'center' }}>
                                <div
                                    onClick={() => addImageRef.current.click()}
                                    style={{ width: 80, height: 80, backgroundColor: 'gray', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', borderRadius: 4 }}>
                                    {imagePreview
                                        ? <img src={imagePreview} alt="preview" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 4 }} />
                                        : <img src={add} style={{ width: 30, height: 30 }} alt="add" />}
                                </div>
                                <input type='file' accept='image/*' style={{ display: 'none' }} ref={addImageRef} onChange={handleImageFileChange} />
                                {imagePreview && (
                                    <div
                                        onClick={() => { setImageFile(null); setImagePreview(''); }}
                                        style={{ width: 25, height: 25, marginLeft: 4, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                        <img src={cross} style={{ width: 20, height: 20 }} alt="remove" />
                                    </div>
                                )}
                            </div>
                        </FormGroup>

                        <FormGroup>
                            <Label>Question</Label>
                            <Input
                                type='textarea'
                                rows={3}
                                placeholder='Enter the quiz question'
                                value={question}
                                onChange={e => setQuestion(e.target.value)}
                            />
                        </FormGroup>

                        <FormGroup>
                            <Label>Options (select the correct one)</Label>
                            {options.map((opt, i) => (
                                <div key={i} style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
                                    <input
                                        type='radio'
                                        name='correctOption'
                                        checked={correctOptionIndex === i}
                                        onChange={() => setCorrectOptionIndex(i)}
                                        style={{ marginRight: 8, accentColor: 'green' }}
                                    />
                                    <Input
                                        type='text'
                                        placeholder={`Option ${i + 1}`}
                                        value={opt}
                                        onChange={e => updateOption(i, e.target.value)}
                                    />
                                </div>
                            ))}
                            <div style={{ fontSize: 12, color: 'green', marginTop: 4 }}>
                                Selected correct answer: Option {correctOptionIndex + 1} — "{options[correctOptionIndex]}"
                            </div>
                        </FormGroup>

                        <FormGroup>
                            <Label>Bonus Fact</Label>
                            <Input
                                type='textarea'
                                rows={2}
                                placeholder='Enter a bonus fact shown after answering'
                                value={bonusFact}
                                onChange={e => setBonusFact(e.target.value)}
                            />
                        </FormGroup>

                        <FormGroup style={{ marginTop: 15, padding: 15, backgroundColor: '#f8f9fa', borderRadius: 8, border: '1px solid #dee2e6' }}>
                            <div style={{ display: 'flex', alignItems: 'center', marginBottom: isScheduled ? 12 : 0 }}>
                                <input
                                    type='checkbox'
                                    id='scheduleToggle'
                                    checked={isScheduled}
                                    onChange={e => { setIsScheduled(e.target.checked); if (!e.target.checked) setScheduledDateTime(''); }}
                                    style={{ marginRight: 8, width: 16, height: 16, cursor: 'pointer' }}
                                />
                                <Label for='scheduleToggle' style={{ margin: 0, cursor: 'pointer', fontWeight: 600 }}>
                                    Schedule for later
                                </Label>
                            </div>
                            {isScheduled && (
                                <div>
                                    <Label style={{ fontSize: 13, color: '#555', marginBottom: 4 }}>
                                        Date &amp; Time — Eastern Time (quiz goes live and users get a push notification)
                                    </Label>
                                    <div style={{ fontSize: 12, color: '#5c6bc0', marginBottom: 6 }}>
                                        Current Eastern time: <strong>{easternNow}</strong>
                                    </div>
                                    <Input
                                        type='datetime-local'
                                        value={scheduledDateTime}
                                        min={epochToEasternWallTime(Date.now() + 60000)}
                                        onChange={e => setScheduledDateTime(e.target.value)}
                                    />
                                </div>
                            )}
                        </FormGroup>

                        <Button
                            className='btn-block btn-dark'
                            style={{ marginTop: 15, fontSize: 16, width: '100%', backgroundColor: isScheduled ? '#5c6bc0' : 'orange', borderWidth: 0, height: 50 }}
                            onClick={handleSave}>
                            {editMode ? 'Update Quiz' : (isScheduled ? 'Schedule Quiz' : 'Save Quiz')}
                        </Button>

                    </Form>
                </div>
            </div>

            {loader && (
                <div style={{ position: 'absolute', display: 'flex', flex: 1, alignItems: 'center', justifyContent: 'center', height: '80%', width: '80%' }}>
                    <div style={{ padding: 10, backgroundColor: 'red', borderRadius: 100 }}>
                        <Loader color="white" height={70} width={70} visible={true} />
                    </div>
                </div>
            )}

            <ToastContainer />
        </div>
    );
};

export default AddQuiz;
